import className from 'classnames';
import { isNil } from 'ramda';
import React, { useEffect } from 'react';
import { MdRemoveCircle } from 'react-icons/md';
import { Button, ConfirmDialog, Label, PlaceholderButton } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import getColCellAlignment from '../Core/getColCellAlignment';
import getColWidth, { GridColWidths } from '../Core/getColWidth';
import './Grid.scss';
import { handleKeyDownWithGridTabbing } from './shared';
import { IGridProps, IVectorProps } from './types';
interface IRowProps extends IVectorProps {
  preventRemoveRow?: boolean;
}

interface IVerticalGridProps extends IGridProps {
  onCreateRow?: () => void;
  onRemoveRow?: (row: number) => void;
  rows?: IRowProps[];
  columns?: IVectorProps[];
  minColWidths?: GridColWidths;
}

/**
 * This implementation follows the API for the Handsontable Grid API. Try to keep them consistent for ease of use.
 * https://handsontable.com/docs/6.2.2/Options.html
 */
const GridBase: React.FC<IVerticalGridProps> = ({
  center,
  colHeaders,
  colWidths,
  minColWidths,
  colCellAlignments,
  columns,
  cornerHeader,
  data,
  disabled,
  hiddenRows,
  hiddenCols,
  onCreateRow,
  onReceivedData,
  onRemoveRow,
  removeConfirm = { confirm: false },
  rowHeaders,
  rowHeaderWidth,
  rows,
  tabDirection = 'horizontal',
}) => {
  // Inform parent of data if it wants to do something with it.
  useEffect(() => onReceivedData && data && onReceivedData(data, hiddenRows), [onReceivedData, data, hiddenRows]);

  /*
   * Row / col helpers
   */
  const showColumnHeaders = colHeaders && colHeaders.length > 0;
  const showRowHeaders = rowHeaders && rowHeaders.length > 0;
  // Corner headers only supported when both row and column headers appear
  // If you need a grid with a corner header in another situation, you will need to "fill in" empty label components
  // for rows/columns as required.
  const showCornerHeader = showColumnHeaders && showRowHeaders;
  const isHiddenRow = (rowIndex: any) => !!(hiddenRows && hiddenRows.includes(rowIndex));
  const isHiddenCol = (colIndex: any) => !!(hiddenCols && hiddenCols.includes(colIndex));
  const showRemoveRowCol = !disabled && onRemoveRow && !rows?.every((r) => r.preventRemoveRow);

  /*
   * All widths / heights are done in JS so we can set them based on properties set by the parent
   */
  const gridHeaderColWidth = rowHeaderWidth ? rowHeaderWidth : 200;
  const gridRowMarginBottom = 5;
  const gridCellHeight = 34;
  const gridCellMarginRight = 5;
  const gridCellRemoveRowWidth = gridCellHeight + 10;
  const gridRowPlaceholderMarginRight =
    showRemoveRowCol && data && data.length > 0
      ? gridCellRemoveRowWidth + gridCellMarginRight * 2
      : gridCellMarginRight;
  const getGridCellStyle = (colIndex: any) => ({
    height: gridCellHeight,
    marginRight: gridCellMarginRight,
    width: getColWidth(colWidths, colIndex),
    minWidth: isNil(minColWidths) ? undefined : getColWidth(minColWidths, colIndex),
    justifyContent: getColCellAlignment(colCellAlignments, colIndex),
  });
  const getGridHeaderStyle = (colIndex: any) => ({
    height: gridCellHeight,
    marginRight: gridCellMarginRight,
    width: getColWidth(colWidths, colIndex),
    minWidth: isNil(minColWidths) ? undefined : getColWidth(minColWidths, colIndex),
  });
  const gridCellRemoveRowStyle = {
    height: gridCellHeight,
    marginRight: gridCellMarginRight,
    width: gridCellRemoveRowWidth,
  };
  const gridCellRowHeaderStyle = { height: gridCellHeight, marginBottom: gridRowMarginBottom };

  /*
   * Custom key handling to move around the grid.
   * Create React references for every input that is not null
   */
  const focusRefs = data?.map((row) => row?.map((col) => col && React.createRef<HTMLElement>()));

  return (
    <div className={className('Grid', { 'Grid-center': center })}>
      {(showRowHeaders || showCornerHeader) && (
        <div className="Grid-HeaderCol" style={{ width: gridHeaderColWidth }}>
          {showCornerHeader && (
            <Label className="Grid-Cell Grid-Cell-rowHeader" style={gridCellRowHeaderStyle}>
              {cornerHeader}
            </Label>
          )}

          {showRowHeaders &&
            rowHeaders?.map(
              (rowHeader: any, rowIndex: any) =>
                !isHiddenRow(rowIndex) && (
                  <Label
                    className="Grid-Cell Grid-Cell-rowHeader"
                    style={gridCellRowHeaderStyle}
                    key={rows?.[rowIndex]?.key ?? rowIndex}
                  >
                    {rowHeader}
                  </Label>
                )
            )}
        </div>
      )}

      <div className="Grid-Viewport">
        <div className="Grid-Rows">
          {showColumnHeaders && (
            <div className="Grid-Row" style={{ marginBottom: gridRowMarginBottom }}>
              {colHeaders?.map(
                (colHeader: any, colIndex: any) =>
                  !isHiddenCol(colIndex) && (
                    <Label
                      className="Grid-Cell Grid-Cell-colHeader"
                      style={getGridHeaderStyle(colIndex)}
                      key={columns?.[colIndex]?.key ?? colIndex}
                    >
                      {colHeader}
                    </Label>
                  )
              )}
            </div>
          )}

          {data?.map(
            (row, rowIndex) =>
              !isHiddenRow(rowIndex) && (
                <div
                  className="Grid-Row"
                  key={rows?.[rowIndex]?.key ?? rowIndex}
                  style={{ marginBottom: gridRowMarginBottom }}
                >
                  {row?.map(
                    (col, colIndex) =>
                      !isHiddenCol(colIndex) && (
                        <div
                          className="Grid-Cell"
                          style={getGridCellStyle(colIndex)}
                          key={columns?.[colIndex]?.key ?? colIndex}
                        >
                          {col &&
                            React.cloneElement(col, {
                              inGrid: true,
                              truncate: true,
                              onKeyDown: handleKeyDownWithGridTabbing(
                                col.props.onKeyDown,
                                tabDirection === 'vertical',
                                focusRefs,
                                focusRefs?.[rowIndex]?.[colIndex],
                                col.props.enterAsTab
                              ),
                              focusRef: focusRefs?.[rowIndex]?.[colIndex],
                            })}
                        </div>
                      )
                  )}

                  {showRemoveRowCol && (
                    <div className="Grid-Cell" style={gridCellRemoveRowStyle}>
                      {!rows?.[rowIndex]?.preventRemoveRow && (
                        <ConfirmDialog {...removeConfirm}>
                          <Button name={`${rowIndex}`} subtle error onClick={() => onRemoveRow?.(rowIndex)}>
                            <MdRemoveCircle size="2em" />
                          </Button>
                        </ConfirmDialog>
                      )}
                    </div>
                  )}
                </div>
              )
          )}

          {onCreateRow && !disabled && (
            <div
              className="Grid-Row Grid-Row-placeholder"
              style={{
                marginBottom: gridRowMarginBottom,
                marginRight: gridRowPlaceholderMarginRight,
                height: gridCellHeight,
              }}
            >
              <PlaceholderButton onClick={onCreateRow} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Grid = withInjectedProps(GridBase);

export { Grid };
export default Grid;
